/** PWA settings defaults + model catalogue (mirrors the Worker's `/v1/models`). */
import type { ModelInfo, ModelSelection, ThinkingEffort, ThinkingMode } from '@luminis/protocol';
import { normalizeDaemonDeviceId, normalizeDaemonE2ePublicKey } from './live/plan';
import type { AppSettings } from './types';

export const MODEL_OPTIONS: readonly ModelInfo[] = [
  {
    provider: 'deepseek',
    model: 'deepseek-flash',
    label: 'DeepSeek Flash',
    supports_tools: true,
    effort_levels: ['low', 'high', 'max'],
  },
  {
    provider: 'deepseek',
    model: 'deepseek-v4-pro',
    label: 'DeepSeek V4 Pro',
    supports_tools: true,
    effort_levels: ['low', 'high', 'max'],
  },
] as const;

export const PERSONA_OPTIONS: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: 'guangyao', label: '光耀', hint: '老友底子：温和、有判断，学术/日程/情感自动切换' },
  { value: 'cute', label: '可爱光耀', hint: '更软更活泼；认真场景自动收起可爱' },
];

export const EFFORT_OPTIONS: ReadonlyArray<{ value: ThinkingEffort; label: string; hint: string }> =
  [
    { value: 'low', label: '低', hint: '更快更省' },
    { value: 'high', label: '高', hint: '平衡' },
    { value: 'max', label: '最高', hint: '默认，最深推理' },
  ];

export const THINKING_OPTIONS: ReadonlyArray<{ value: ThinkingMode; label: string }> = [
  { value: 'enabled', label: '开启思考' },
  { value: 'disabled', label: '关闭思考' },
];

export function defaultWorkerUrl(): string {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
  return meta.env?.VITE_WORKER_URL ?? '';
}

/** Build-time defaults for the live relay target (both values are public). */
export function defaultDaemonDeviceId(): string {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
  return normalizeDaemonDeviceId(meta.env?.VITE_DAEMON_DEVICE_ID ?? '');
}

export function defaultDaemonE2ePublicKey(): string {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
  return normalizeDaemonE2ePublicKey(meta.env?.VITE_DAEMON_E2E_PUBLIC_KEY ?? '');
}

export const DEFAULT_SETTINGS: AppSettings = {
  workerUrl: defaultWorkerUrl(),
  demoMode: true,
  displayName: '我的手机',
  model: { provider: 'deepseek', model: 'deepseek-flash' },
  personaId: 'guangyao',
  thinking: 'enabled',
  effort: 'max',
  notificationsEnabled: false,
  hapticsEnabled: true,
  maxToolRounds: 16,
  daemonDeviceId: defaultDaemonDeviceId(),
  daemonE2ePublicKey: defaultDaemonE2ePublicKey(),
};

export function normalizeWorkerUrl(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/u, '');
  if (!trimmed) return '';
  if (!/^https?:\/\//iu.test(trimmed)) return '';
  try {
    const url = new URL(trimmed);
    return url.origin + (url.pathname === '/' ? '' : url.pathname.replace(/\/+$/u, ''));
  } catch {
    return '';
  }
}

/** Merge stored settings with defaults; ignores unknown/invalid values. */
export function settingsWithDefaults(stored: Partial<AppSettings> | null | undefined): AppSettings {
  if (!stored || typeof stored !== 'object') return { ...DEFAULT_SETTINGS };
  const model = stored.model;
  const knownModel =
    model && MODEL_OPTIONS.some((option) => option.model === model.model)
      ? { provider: model.provider ?? 'deepseek', model: model.model }
      : DEFAULT_SETTINGS.model;
  const personaId =
    typeof stored.personaId === 'string' &&
    PERSONA_OPTIONS.some((option) => option.value === stored.personaId)
      ? stored.personaId
      : DEFAULT_SETTINGS.personaId;
  const thinking: ThinkingMode =
    stored.thinking === 'disabled' || stored.thinking === 'enabled'
      ? stored.thinking
      : DEFAULT_SETTINGS.thinking;
  const effort: ThinkingEffort =
    stored.effort === 'low' || stored.effort === 'high' || stored.effort === 'max'
      ? stored.effort
      : DEFAULT_SETTINGS.effort;
  return {
    ...DEFAULT_SETTINGS,
    ...stored,
    model: knownModel,
    personaId,
    thinking,
    effort,
    demoMode: stored.demoMode ?? DEFAULT_SETTINGS.demoMode,
    maxToolRounds:
      typeof stored.maxToolRounds === 'number' &&
      Number.isInteger(stored.maxToolRounds) &&
      stored.maxToolRounds >= 1 &&
      stored.maxToolRounds <= 50
        ? stored.maxToolRounds
        : DEFAULT_SETTINGS.maxToolRounds,
    workerUrl: normalizeWorkerUrl(stored.workerUrl ?? ''),
    daemonDeviceId: normalizeDaemonDeviceId(
      stored.daemonDeviceId ?? DEFAULT_SETTINGS.daemonDeviceId,
    ),
    daemonE2ePublicKey: normalizeDaemonE2ePublicKey(
      stored.daemonE2ePublicKey ?? DEFAULT_SETTINGS.daemonE2ePublicKey,
    ),
  };
}

export function modelSelection(settings: AppSettings): ModelSelection {
  return {
    provider: settings.model.provider,
    model: settings.model.model,
    thinking: settings.thinking,
    effort: settings.effort,
  };
}

export function describeModel(settings: AppSettings): string {
  const option = MODEL_OPTIONS.find((entry) => entry.model === settings.model.model);
  const effort =
    EFFORT_OPTIONS.find((entry) => entry.value === settings.effort)?.label ?? settings.effort;
  const thinking = settings.thinking === 'enabled' ? `思考·${effort}` : '不思考';
  return `${option?.label ?? settings.model.model} · ${thinking}`;
}

export function estimateCost(usage: { input_tokens: number; output_tokens: number }): number {
  // Rough DeepSeek pricing placeholder used for the usage ledger UI (CNY per 1M tokens).
  const inputPerMillion = 2;
  const outputPerMillion = 8;
  return (
    (usage.input_tokens / 1_000_000) * inputPerMillion +
    (usage.output_tokens / 1_000_000) * outputPerMillion
  );
}
